// src/forms/data_hub_forms.js
import merge from 'lodash.merge';
import cloneDeep from 'lodash.clonedeep';
import {
    fetch_with_timeout,
    parse_boolean_falsy_types,
} from '../helpers';

/**
 * Thrown by a consumer-provided hook (pre_render_function, pre_submit_function,
 * return_function) to abort the rest of the form lifecycle. The library catches it,
 * clears the loading state, and surfaces both messages via error_function. The
 * consumer should NOT also call error_function — the library does it.
 *
 * Any non-DataHubFormAbort throw from a consumer hook is treated as a bug and routed
 * to error_function with a stack-trace internal message and a generic external one.
 *
 * Usage:
 *     throw new DataHubFormAbort(
 *         'pre_submit: no valid message_type_ids found, raw values=' + JSON.stringify(values),
 *         'Sorry, we could not submit your subscription preferences. Please contact an administrator.'
 *     );
 */
export class DataHubFormAbort extends Error {
    constructor(internal_error_message, external_error_message) {
        super(internal_error_message);
        this.name = 'DataHubFormAbort';
        this.internal_error_message = internal_error_message;
        this.external_error_message = external_error_message;
    }
}

/**
 * Centralizes the lib's response to a throw from a consumer hook. Always clears
 * loading state so the form is interactive again. Routes the abort/error through
 * the consumer's error_function (which logs + tracks).
 */
function handle_consumer_hook_throw(error, form_container, form_instance, hook_name) {
    try {
        remove_form_input_loading_state(form_container);
        remove_form_loading_overlay(form_container, form_instance?.loading_overlay_selector, form_instance?.data_hub);
        remove_submit_button_loading_state(form_container, form_instance?.submit_button_selector);
    } catch (_cleanup_error) {
        // Cleanup helpers should never throw; if they do, swallow so we still report the original error.
    }

    if (error instanceof DataHubFormAbort) {
        form_instance?.error_function?.(
            `[${hook_name}] aborted: ${error.internal_error_message || ''}`,
            error.external_error_message
        );
        return;
    }

    form_instance?.error_function?.(
        `Unexpected error in ${hook_name}: ` + (error?.stack || error?.message || String(error)),
        'Sorry, something went wrong. Please refresh the page or contact an administrator.'
    );
}

const disallowed_form_submit_user_data_key_names = [
    'subscribedChannelIds',
    'unsubscribedChannelIds',
];

/**
 * Removes user_data fields that hosted/signup forms must never submit. These
 * keys still exist in older shared/static person contracts for legacy
 * subscription-preferences surfaces, but hosted forms cannot send channel IDs
 * because iterable-api-process does not allow channel-level subscription writes.
 *
 * @param {Object} user_data - Candidate person data from form inputs or cache.
 * @returns {Object} A cloned user_data object without disallowed form-submit keys.
 */
function remove_disallowed_form_submit_user_data_keys(user_data = {}) {
    if (!user_data || typeof user_data !== 'object') {
        return {};
    }

    const user_data_without_disallowed_form_submit_keys = cloneDeep(user_data);

    disallowed_form_submit_user_data_key_names.forEach(disallowed_key_name => {
        delete user_data_without_disallowed_form_submit_keys[disallowed_key_name];
    });

    return user_data_without_disallowed_form_submit_keys;
}

/**
 * Gets the cached user_data keys that should be removed when a visible form
 * person field was submitted as blank. Most fields clear only their exact key,
 * but the legacy phone aliases all represent the same browser-visible phone
 * input in generated hosted forms, so clearing one should keep the other cached
 * aliases from silently re-identifying the submit.
 *
 * @param {string} cleared_form_user_data_key_name - The submitted blank person field key.
 * @returns {Array<string>} The cached user_data keys to remove before submit merging.
 */
function get_cached_user_data_keys_for_blank_form_field(cleared_form_user_data_key_name) {
    if (cell_phone_names.includes(cleared_form_user_data_key_name)) {
        return cell_phone_names;
    }

    return [cleared_form_user_data_key_name];
}

/**
 * Removes cached user_data values for person fields that the current form
 * submitted as blank. This keeps the live form values authoritative over
 * localStorage / instance user_data for the fields the visitor can see and edit.
 *
 * @param {Object} user_data - Cached person data from the Data Hub instance or window cache.
 * @param {Set<string>} blank_form_user_data_key_names - Person field keys submitted as blank.
 * @returns {Object} A cloned user_data object with blank submitted fields removed.
 */
function remove_blank_form_fields_from_cached_user_data(user_data = {}, blank_form_user_data_key_names = new Set()) {
    if (!user_data || typeof user_data !== 'object') {
        return {};
    }

    const user_data_without_blank_form_fields = cloneDeep(user_data);

    blank_form_user_data_key_names.forEach(blank_form_user_data_key_name => {
        const cached_user_data_key_names_to_clear = get_cached_user_data_keys_for_blank_form_field(blank_form_user_data_key_name);

        cached_user_data_key_names_to_clear.forEach(cached_user_data_key_name => {
            delete user_data_without_blank_form_fields[cached_user_data_key_name];
        });
    });

    return user_data_without_blank_form_fields;
}

import {
    get_metadata_form_fields,
    get_custom_data_form_fields
} from '../helpers/form_process_functions';
import { process_bad_form_response } from '../forms/html_element_helpers/process_bad_response';
import {
    white_list_incoming_person_fields,
    allowed_all_hmac_key_names,
    form_input_types_string,
    empty_array_keys_to_skip,
    at_least_one_required_form_identifier_fields,
    default_selectors,
    cell_phone_names
} from '../helpers/static_vars';
import { apply_server_forgotten_state } from '../analytics/privacy';
import {
    showRedBorderRequiredFieldsMissingValues,
    generateMissingRequiredFieldsString,
    changeElementErrorStatus,
    verify_action_array,
    validateAllRequiredFields
} from '../helpers/form_validation';
import {
    add_form_input_loading_state,
    remove_form_input_loading_state,
    add_form_loading_overlay,
    remove_form_loading_overlay,
    add_submit_button_loading_state,
    remove_submit_button_loading_state,
    prefill_form,
    re_prefill_all_forms,
    return_error_clear_form_loading_state,
    hide_on_submit
} from '../forms/shared_form_ux_functions';
import { create_person_format_modifications_ux_elements } from "../forms/html_element_helpers/validation_ui_elements";
import { rebuild_iterable_subscription_events_for_submit } from './html_element_helpers/form_functions';
import { build_data_hub_action_privacy_headers } from '../helpers/data_hub_privacy_policy';
import { merge_user_data_browser } from '../helpers/user_data_smart_merge';
import { is_under_18, cleanUserData } from '../helpers/get_process_user_data';
import { userDataRequiresAtLeastOneIdField, validateEmail } from '../forms/html_element_helpers/user_data_validation';
import { process_verify_tel_input_values } from '../tel/telephone_sms_inputs';
import { enqueue_user_data_cache_sync } from '../helpers/user_data_cache_sync';

// INDIVIDUAL FORMS
export class data_hub_form {
    /**
     * Constructs a new data_hub_form instance.
     * @param {Object} config - Configuration object.
     * @param {Object} [config.data_hub_instance] - Instance of data_hub (or defaults to window.data_hub).
     * @param {number} [config.prefill_timeout] - Timeout for pre-filling the form.
     * @param {number} [config.post_person_timeout] - Timeout for the form POST request.
     * @param {string} [config.prefill_input_selector] - Selector for form fields to prefill.
     * @param {Function} [callback_function_after_init] - Callback to run after initialization.
     */
    constructor(config = {}, callback_function_after_init = () => { }) {
        // CONFIG
        this.data_hub = config?.data_hub_instance || window.data_hub;
        this.prefill_timeout = config?.prefill_timeout || this.data_hub.prefill_timeout || 1300;
        // Default 45s — tolerates Cloud Run cold starts on person-format (~14s) plus
        // Iterable fan-out and BigQuery write. The previous 10s default was timing out
        // on cold starts, which silently surfaced as a "Cannot read properties of
        // undefined (reading 'ok')" crash because fetch_with_timeout used to swallow
        // AbortError and return undefined. Both issues fixed; this is the new ceiling.
        this.post_person_timeout = config?.post_person_timeout || 45000;
        this.prefill_input_selector = config?.prefill_input_selector || this.data_hub.prefill_input_selector
            || default_selectors.prefill_input_selector;
        this.loading_overlay_selector = config?.loading_overlay_selector || default_selectors.loading_overlay_selector;

        // Shared or global data_hub functions
        this.pre_render_function = config?.pre_render_function || (() => { });
        this.pre_submit_function = config?.pre_submit_function || (() => { });
        this.error_function = config?.error_function || this.data_hub?.error_function ||
            ((internal_error_message, external_error_message) => { console.error(internal_error_message, external_error_message); });
        this.return_function = config?.return_function || (() => { }); //((document, results, self) => { console.log(results); });
        this.unload_function = config?.unload_function || this.data_hub?.unload_function ||
            (() => { });

        if (!this.data_hub) {
            return this.error_function?.('No data_hub found', 'Please contact support');
        }

        this.init_data(callback_function_after_init);
    }

    /**
     * Initializes data and calls the callback after initialization.
     * @param {Function} callback_function_after_init - Callback to invoke after data initialization.
     */
    async init_data(callback_function_after_init) {
        if (typeof callback_function_after_init === 'function') {
            callback_function_after_init();
        }
    }

    /**
     * Initializes the form by binding event listeners, pre-filling data, and setting up metadata.
     * @param {Object} settings - Configuration settings for the form.
     */
    init(settings = {}) {
        // Select the standard form elements to attach click events
        this.form_container_selector = settings.form_container_selector || default_selectors.form_container_selector;
        this.form_containers = settings.form_container
            ? (settings.form_container instanceof HTMLElement ? [settings.form_container] : settings.form_container)
            : document.querySelectorAll(this.form_container_selector);

        this.submit_button_selector = settings.submit_button_selector || default_selectors.submit_button_selector;
        this.submit_buttons = settings.submit_button
            ? (settings.submit_button instanceof HTMLElement ? [settings.submit_button] : settings.submit_button)
            : Array.from(document.querySelectorAll(this.submit_button_selector));

        this.loading_overlay_selector = settings.loading_overlay_selector || default_selectors.loading_overlay_selector;

        this.reset_button_selector = settings.reset_button_selector || default_selectors.reset_button_selector;
        this.hide_on_submit_selector = settings.hide_on_submit_selector || default_selectors.hide_on_submit_selector;
        this.form_metadata_form_field_selector = settings.metadata_form_field_selector || default_selectors.metadata_form_field_selector;
        this.form_custom_data_form_field_selector = settings.custom_data_form_field_selector || default_selectors.custom_data_form_field_selector;

        // Form data
        this.events = settings.events || [];
        this.iterable_subscription_form_context = settings.iterable_subscription_form_context || null;
        this.returns = settings.returns || {};
        this.form_metadata = settings.form_metadata || {};
        this.custom_data = settings.custom_data || {};

        // Settings
        this.return_submit_function_immediately = settings.return_submit_function_immediately || false;
        this.show_person_format_modifications_ux_elements =
            settings.show_person_format_modifications_ux_elements !== undefined
                ? settings.show_person_format_modifications_ux_elements
                : (this.data_hub?.show_person_format_modifications_ux_elements !== undefined
                    ? this.data_hub.show_person_format_modifications_ux_elements
                    : false);

        // Functions
        this.pre_render_function = settings.pre_render_function || this.pre_render_function || (() => { });
        this.pre_submit_function = settings.pre_submit_function || this.pre_submit_function || (() => { });
        this.error_function = settings.error_function || this.data_hub?.error_function ||
            ((internal_error_message, external_error_message) => { console.error(internal_error_message, external_error_message); });
        this.return_function = settings.return_function || this.return_function || (() => { });
        this.unload_function = settings.unload_function || this.unload_function || (() => { });

        // Pre Fills
        this.has_at_least_one_prefill = false;
        this.allow_prefill = settings.allow_prefill !== undefined ? settings.allow_prefill : true;

        // Check for existing form containers
        if (!this.form_containers || this.form_containers.length === 0) {
            return this.error_function?.('No form containers found', 'Please contact support');
        }

        // Ensure form_containers is always an array
        if (!Array.isArray(this.form_containers)) {
            this.form_containers = Array.from(this.form_containers);
        }

        // Bind submit and reset events for each form based on passed-in selectors
        this.form_containers.forEach(form_container => {
            try {
                this.pre_render_function(form_container, this);
            } catch (error) {
                handle_consumer_hook_throw(error, form_container, this, 'pre_render_function');
                return; // skip binding submit handlers — render didn't complete
            }

            let bound_at_least_one_submit = false;
            const forms = form_container.querySelectorAll('form');
            forms.forEach(form => {
                // Default submit events, to capture clicks even if mismatched from passed selector
                if (form.querySelector('input[type="submit"], button[type="submit"], input[type="image"]')) {
                    bound_at_least_one_submit = true;
                }

                form.addEventListener('submit', (event) => {
                    event.preventDefault();
                    this.submit_form_data(event, form_container);
                });
            });

            // Handling potential double-binding issue
            const submit_buttons = this.submit_buttons || form_container.querySelectorAll(this.submit_button_selector);
            submit_buttons.forEach(submit_button => {
                if (submit_button && !submit_button.dataset.bound) {
                    submit_button.dataset.bound = 'true';  // Flag to indicate binding
                    bound_at_least_one_submit = true;
                    submit_button.onclick = (event) => {
                        event.preventDefault();
                        this.submit_form_data(event, form_container);
                    };
                }
            });

            // If no bound submits, return error
            if (!bound_at_least_one_submit) {
                return this.error_function?.('No submit buttons found', 'Please contact support');
            }

            // Prefill form fields if applicable
            if (this.allow_prefill || this.data_hub.prefill_all_forms) {
                if (this.data_hub.user_data_loaded_promise) {
                    this.data_hub.user_data_loaded_promise.then(() => {
                        prefill_form(
                            settings.form_container || document,
                            this.data_hub?.user_data || {},
                            this.prefill_input_selector,
                            this.reset_button_selector
                        );
                    }).catch((error) => {
                        this.error_function?.(`Error loading user data: ${error}`, null);
                    });
                } else {
                    prefill_form(settings.form_container || document, {}, this.prefill_input_selector);
                }
            } else {
                prefill_form(settings.form_container || document, {}, this.prefill_input_selector);
            }
        });

        // TODO setup and test in future if (this.unload_function) {
    }

    /**
     * Handles form submission: processes user input, updates the central user_data, validates required fields,
     * merges metadata, and communicates with the API.
     *
     * IMPORTANT: Ensure that user_data is updated with the latest form values. For example, if the global data_hub.user_data.first_name
     * was "old name" and the input field now contains "dillon", this value should be updated accordingly.
     *
     * @param {Event} event - The form submission event.
     * @param {HTMLElement} form_container - The container element for the form.
     * @param {Object} [returns] - An object describing the expected HTTP endpoint returns. The default is:
     * @param {string} [returns.user_data] - A string template for user data, e.g., "{{user_data}}".
     * @param {string} [returns.metadata] - A string template for metadata, e.g., "{{metadata}}".
     * @param {string} [returns.third_party_results] - A string template for third-party results, e.g., "{{third_party_results}}".
     * @param {string} [returns.event_uuid] - A string template for the event UUID, e.g., "{{event_uuid}}".
     * @param {string} [returns.jwt_cookie] - A string template for the JWT cookie, e.g., "{{jwt_cookie}}".
     * @returns {Promise<*>} The result of the return_function, or an error if submission fails.
     */

    async submit_form_data(
        event,
        form_container,
        returns = {
            user_data: "{{user_data}}", metadata: "{{metadata}}",
            third_party_results: "{{third_party_results}}", event_uuid: "{{event_uuid}}", jwt_cookie: "{{jwt_cookie}}"
        }
    ) {
        let response_data = {};
        let already_called_return_function = false;
        let submission_had_error = false;
        let submission_error_result = null;

        try {
            event.preventDefault();

            if (!form_container) {
                return this.error_function?.('No form container found', 'Please contact support');
            }

            add_submit_button_loading_state(form_container, this.submit_button_selector);
            add_form_input_loading_state(form_container, form_input_types_string, this.data_hub);
            add_form_loading_overlay(form_container, this.loading_overlay_selector, this.data_hub);

            let form_elements = form_container.querySelectorAll(form_input_types_string);
            if (!form_elements || !form_elements.length) {
                let good_to_submit = false;
                // Check that user_data has a key name from at_least_one_required_form_identifier_fields
                if (this.data_hub?.user_data) {
                    const has_one_required_user_data_field = at_least_one_required_form_identifier_fields.some(field => {
                        return this.data_hub.user_data[field];
                    });
                    if (has_one_required_user_data_field) {
                        // Create a local array of form inputs using required fields from user_data
                        form_elements = at_least_one_required_form_identifier_fields.map(field => {
                            const input = document.createElement('input');
                            input.name = field;
                            input.value = this.data_hub.user_data[field] || '';
                            return input;
                        });
                        good_to_submit = true;
                    }
                }
                if (!good_to_submit) {
                    return return_error_clear_form_loading_state(
                        form_container,
                        'No form elements found',
                        'Please enter an email, phone, external ID, or supported hash identifier into any of the inputs on this page.',
                        this.data_hub
                    );
                }
            }

            let this_new_user_data = {};
            let form_inputs_marked_required = [];
            let blank_form_user_data_key_names = new Set();

            Array.from(form_elements).forEach(element => {
                const key = element.id || element.name;
                if (
                    key
                    && white_list_incoming_person_fields.includes(key)
                    && !disallowed_form_submit_user_data_key_names.includes(key)
                ) {
                    if (['checkbox', 'radio'].includes(element.type)) {
                        this_new_user_data[key] = parse_boolean_falsy_types(element.checked);
                    } else {
                        this_new_user_data[key] = element.value.trim();

                        // Blank visible person fields should beat cached localStorage
                        // values for the same logical field. Without this, a cleared
                        // generated cell_phone field can still submit a cached phoneNumber.
                        if (this_new_user_data[key] === '') {
                            blank_form_user_data_key_names.add(key);
                        }
                    }

                    if (element.required) {
                        form_inputs_marked_required.push(element.name || element.id);
                    }
                }
            });

            const mergedRequiredNames = [...new Set([...form_inputs_marked_required, ...at_least_one_required_form_identifier_fields])];

            // Validate ALL required fields (including non-primary identifiers like first_name)
            // This checks the element.required attribute directly on form elements
            // Also ensures at least one configured form identifier is present.
            const { valid: allRequiredFieldsValid, missing: missingAllRequiredFields } = validateAllRequiredFields(
                form_elements,
                at_least_one_required_form_identifier_fields,
                this.data_hub
            );

            if (!allRequiredFieldsValid) {
                const formFieldNamesWithMissingValues = showRedBorderRequiredFieldsMissingValues(form_elements, mergedRequiredNames, this.data_hub);
                const readableMissingFieldsString = generateMissingRequiredFieldsString(formFieldNamesWithMissingValues);
                return return_error_clear_form_loading_state(
                    form_container,
                    `Missing required fields: ${readableMissingFieldsString}`,
                    `Please complete the following fields: ${readableMissingFieldsString || missingAllRequiredFields.join(', ')}`,
                    this.data_hub
                );
            }

            let telInputsWithBadValues = [];
            let errorMessages = [];
            const processedTelInputs = await process_verify_tel_input_values(form_elements, this.error_function);

            if (processedTelInputs && Array.isArray(processedTelInputs) && processedTelInputs.length > 0) {
                processedTelInputs.forEach(singleTelInputResponseObject => {

                    // Metadata / custom_data-marked tel inputs must never write the visitor's
                    // person country fields — their country belongs to the marked target only.
                    const el = singleTelInputResponseObject.domElement;
                    const is_non_person_tel = el?.hasAttribute('data-hub-form-metadata-field')
                        || el?.hasAttribute('data-hub-form-custom-data-field');

                    // For some reason intl tel will return 'null' or 'undefined' as strings so do not write those...
                    if (
                        !is_non_person_tel &&
                        singleTelInputResponseObject.country_code &&
                        singleTelInputResponseObject.country_code.toLowerCase() !== 'null' &&
                        singleTelInputResponseObject.country_code.toLowerCase() !== 'undefined' &&
                        this_new_user_data.country_code !== singleTelInputResponseObject.country_code
                    ) {
                        this_new_user_data.country_code = singleTelInputResponseObject.country_code;
                    }
                    if (
                        !is_non_person_tel &&
                        singleTelInputResponseObject.country &&
                        singleTelInputResponseObject.country.toLowerCase() !== 'null' &&
                        singleTelInputResponseObject.country.toLowerCase() !== 'undefined' &&
                        this_new_user_data.country !== singleTelInputResponseObject.country
                    ) {
                        this_new_user_data.country = singleTelInputResponseObject.country;
                    }

                    // Only track validation errors for required fields or non-empty optional fields
                    if (!singleTelInputResponseObject.isValid &&
                        (singleTelInputResponseObject.isRequired || !singleTelInputResponseObject.isEmpty)) {
                        telInputsWithBadValues.push(singleTelInputResponseObject.domElement.id || singleTelInputResponseObject.domElement.name);
                        changeElementErrorStatus(singleTelInputResponseObject.domElement, true);
                        if (singleTelInputResponseObject.errorMessage) {
                            errorMessages.push(singleTelInputResponseObject.errorMessage);
                        }
                    } else {
                        changeElementErrorStatus(singleTelInputResponseObject.domElement, false);
                    }
                });

                if (telInputsWithBadValues.length > 0) {
                    const readableBadTelFieldsString = generateMissingRequiredFieldsString(telInputsWithBadValues);
                    const concatenatedErrorMessages = errorMessages.join(', ');

                    return return_error_clear_form_loading_state(
                        form_container,
                        `Invalid phone numbers: ${readableBadTelFieldsString}`,
                        `Please fix these phone numbers: ${readableBadTelFieldsString}.<br>${concatenatedErrorMessages}`,
                        this.data_hub
                    );
                }
            }

            // Do a very basic email check
            // Will NOT return false if empty. Returns true. We should already be checking for that above in required
            const { valid: emailValid, error_message: invalidEmailMessage } = validateEmail(this_new_user_data.email);
            if (!emailValid) {
                return return_error_clear_form_loading_state(
                    form_container,
                    'Invalid email address',
                    invalidEmailMessage || 'Please enter a valid email address',
                    this.data_hub
                );
            }

            if (window?.data_hub_user_data) {
                this.data_hub.user_data = await merge_user_data_browser(
                    this.data_hub.user_data || {},
                    window.data_hub_user_data
                );
            } else {
                window.data_hub_user_data = this.data_hub?.user_data || {};
            }

            if (!this.data_hub?.user_data?.cell_phone && this.data_hub?.user_data?.phoneNumber) {
                this.data_hub.user_data.cell_phone = this.data_hub.user_data.phoneNumber;
                window.data_hub_user_data.cell_phone = this.data_hub.user_data.phoneNumber;
            }

            // Pass `this` (the form instance — it owns form_metadata_form_field_selector,
            // set in init), NOT this.data_hub: the DataHub instance has no selector so the
            // harvest silently returned {}.
            // Harvested metadata-marked inputs win over the static form/page metadata blobs
            // (same harvested-wins precedence as the custom_data lane below).
            const merged_metadata = merge(
                {},
                this.form_metadata,
                this.data_hub?.page_metadata,
                get_metadata_form_fields(form_container, this)
            );

            const events_for_submit = rebuild_iterable_subscription_events_for_submit(
                this.events,
                this.iterable_subscription_form_context,
                this.data_hub
            );

            let data_hub_action_object_to_send_to_api = {
                events: events_for_submit,
                returns: returns,
                // Harvested custom_data-marked inputs win over the static custom_data blob.
                custom_data: merge({}, this.custom_data, get_custom_data_form_fields(form_container, this)),
                user_data: {},
                metadata: merged_metadata,
            };

            if (this.data_hub.organization_id) {
                data_hub_action_object_to_send_to_api.organization_id = this.data_hub.organization_id.trim();
            }

            for (const [key, value] of this.data_hub.url_params) {
                if (allowed_all_hmac_key_names.includes(key)) {
                    data_hub_action_object_to_send_to_api.user_data[key] = value;
                }
            }

            // Append additional metadata if present.
            if (this.data_hub?.iterableCampaignId) {
                data_hub_action_object_to_send_to_api.metadata.iterableCampaignId = this.data_hub.iterableCampaignId;
            }
            if (this.data_hub?.iterableTemplateId) {
                data_hub_action_object_to_send_to_api.metadata.iterableTemplateId = this.data_hub.iterableTemplateId;
            }
            if (!events_for_submit || events_for_submit.length === 0) {
                return return_error_clear_form_loading_state(
                    form_container,
                    'No events to submit',
                    "Please contact an administrator",
                    this.data_hub
                );
            }

            const is_valid_action_array = verify_action_array(events_for_submit, this.error_function);
            if (is_valid_action_array !== true) {
                return return_error_clear_form_loading_state(
                    form_container,
                    'Invalid action array',
                    `Please contact an administrator`,
                    this.data_hub
                );
            }

            const cached_instance_user_data = remove_disallowed_form_submit_user_data_keys(
                remove_blank_form_fields_from_cached_user_data(
                    this.data_hub?.user_data || {},
                    blank_form_user_data_key_names
                )
            );
            const cached_window_user_data = remove_disallowed_form_submit_user_data_keys(
                remove_blank_form_fields_from_cached_user_data(
                    window?.data_hub_user_data || {},
                    blank_form_user_data_key_names
                )
            );

            if (this.data_hub) {
                this.data_hub.user_data = cached_instance_user_data;
            }
            if (typeof window !== 'undefined' && window.data_hub_user_data) {
                window.data_hub_user_data = cached_window_user_data;
            }

            let merged_for_submit = await merge_user_data_browser(
                {},
                cached_instance_user_data
            );
            merged_for_submit = await merge_user_data_browser(
                merged_for_submit,
                cached_window_user_data
            );
            this_new_user_data = await merge_user_data_browser(merged_for_submit, this_new_user_data || {});

            this_new_user_data = cleanUserData(this_new_user_data, empty_array_keys_to_skip);
            data_hub_action_object_to_send_to_api.user_data = this_new_user_data;

            try {
                this.pre_submit_function(form_container, data_hub_action_object_to_send_to_api, this.data_hub);
            } catch (error) {
                handle_consumer_hook_throw(error, form_container, this, 'pre_submit_function');
                return; // bail out — submission aborted
            }

            this_new_user_data = cleanUserData(this_new_user_data, empty_array_keys_to_skip);
            const { valid: valid2, missing: missing2 } = userDataRequiresAtLeastOneIdField(this_new_user_data, mergedRequiredNames, this.error_function);
            if (!valid2) {

                const formFieldNamesWithMissingValues = showRedBorderRequiredFieldsMissingValues(form_elements, mergedRequiredNames, this.data_hub);
                const readableMissingFieldsString = generateMissingRequiredFieldsString(formFieldNamesWithMissingValues);

                return return_error_clear_form_loading_state(
                    form_container,
                    'Missing required fields' + (readableMissingFieldsString ? `: ${readableMissingFieldsString}` : ''),
                    `Please complete the following fields: ${readableMissingFieldsString || missing2.join(', ')}`,
                    this.data_hub
                );
            }

            // Merge back to user_data now? It will on the returned so this is kind of pointless. BUT maybe for errors?
            data_hub_action_object_to_send_to_api.user_data = this_new_user_data;
            this.data_hub.user_data = this_new_user_data;
            window.data_hub_user_data = this_new_user_data;

            if (is_under_18(data_hub_action_object_to_send_to_api?.user_data || this.data_hub?.user_data, this.data_hub)) {
                // Compliance: under-18 user. We must (a) demote consent to the narrowest
                // mode that still allows the necessary unknown_uuid JWT cookie, and
                // (b) clear all local + remote state so the under-18's data isn't retained.
                if (this.data_hub) {
                    this.data_hub.privacy_policy = 'necessary';
                    if (typeof this.data_hub.forget_user === 'function') {
                        // Hits /api/clear-jwt → server returns a fresh unknown_uuid JWT.
                        // Also clears window.data_hub_user_data, instance.user_data, and
                        // blanks all visible form inputs.
                        this.data_hub.forget_user({
                            clear_remote_cookie: true,
                            clear_local_user_data: true,
                            re_prefill_forms_after_clear: true
                        });
                    } else {
                        // Defensive fallback if forget_user isn't wired (older instances).
                        this.data_hub.user_data = {};
                        window.data_hub_user_data = {};
                    }
                }
                return_error_clear_form_loading_state(
                    form_container,
                    'User is under 18',
                    'You must be 18 or older to submit this form.',
                    this.data_hub
                );
                return;
            }

            if (!this.data_hub.forms_domain) {
                return return_error_clear_form_loading_state(
                    form_container,
                    'No forms domain found',
                    'Please contact an administrator',
                    this.data_hub
                );
            }

            // Keep the URL param approach as a fallback
            let user_data_api_url = `${this.data_hub.forms_domain}/api/post/user_data`;

            // If using the "submit function immediately" flow, do it now.
            if (this.return_submit_function_immediately) {
                hide_on_submit(this.hide_on_submit_selector, form_container);
                try {
                    this.return_function(form_container, data_hub_action_object_to_send_to_api, this.data_hub);
                    already_called_return_function = true;
                } catch (error) {
                    handle_consumer_hook_throw(error, form_container, this, 'return_function (return_submit_function_immediately)');
                    return; // consumer aborted — skip the actual API call
                }
            }

            // use cloneDeep to avoid mutating the original user_data object
            const original_user_data = cloneDeep(data_hub_action_object_to_send_to_api?.user_data || {});

            try {
                // Create headers object with required content type
                const headers = {
                    'Content-Type': 'application/json',
                    ...build_data_hub_action_privacy_headers(this.data_hub),
                };

                // If the JWT cookie exists, add it as an Authorization Bearer token
                // and add it to the request body if needed
                if (this.data_hub.jwt_cookie) {
                    //headers['Authorization'] = `Bearer ${this.data_hub.jwt_cookie}`;
                    data_hub_action_object_to_send_to_api.jwt = this.data_hub.jwt_cookie;
                }

                // Make the fetch request
                // 250327 trying with credentials included. BUT TODO i dont think we need? we arent passing a bearer header?
                // OPENAI: would this make it more likely for cookies to be blocked?
                //
                // abort_on_timeout: false — if the timer fires, we surface a TimeoutError
                // immediately but DO NOT cancel the underlying POST. The server may still
                // be writing to BigQuery / Iterable, and a late 200 (or late error) is far
                // more useful for debugging than a canceled connection. Late settles are
                // routed to error_function so they show up in our normal error pipeline.
                const post_started_at = Date.now();
                const response = await fetch_with_timeout(user_data_api_url, {
                    method: 'POST',
                    headers: headers,
                    credentials: 'include',
                    body: JSON.stringify(data_hub_action_object_to_send_to_api),
                    timeout: this.post_person_timeout || 45000,
                    abort_on_timeout: false,
                    on_late_response: ({ response: late_response, error: late_error, elapsed_ms }) => {
                        if (late_error) {
                            this.data_hub?.error_function?.(
                                `POST user_data late error after timeout (${elapsed_ms}ms total): ${late_error?.message || late_error}`,
                                null
                            );
                            return;
                        }
                        if (late_response) {
                            this.data_hub?.error_function?.(
                                `POST user_data late response after timeout (${elapsed_ms}ms total): status ${late_response.status} ${late_response.statusText || ''}`.trim(),
                                null
                            );
                        }
                    }
                });
                // Reference post_started_at so linters don't flag an unused var if the
                // timeout path takes over; useful for future correlation logs.
                void post_started_at;

                if (!response.ok || response.status !== 200) {
                    submission_had_error = true;
                    submission_error_result = process_bad_form_response(
                        response,
                        form_container,
                        default_selectors.error_container_selector,
                        this.data_hub
                    );
                } else {
                    try {
                        response_data = await response.json();
                    } catch (json_parse_error) {
                        // 200 OK but body wasn't valid JSON. Outer catch would surface
                        // "Unexpected end of JSON input — try again later" which misleads users
                        // into pointless retries when the actual issue is server-side.
                        // Log the raw body (truncated) so the bug is debuggable from error tracking.
                        let raw_body_snippet = '';
                        try {
                            raw_body_snippet = (await response.clone().text()).slice(0, 500);
                        } catch (_) { /* response already consumed */ }
                        this.data_hub?.error_function?.(
                            `Submission succeeded with status ${response.status} but response body was not valid JSON: ${json_parse_error?.message || json_parse_error}. Body snippet: ${raw_body_snippet}`,
                            null
                        );
                        return return_error_clear_form_loading_state(
                            form_container,
                            'Server returned an unexpected response',
                            'Your submission may have been received, but the server response could not be read. Please contact support if you don\'t hear back.',
                            this.data_hub
                        );
                    }

                    // Optionally check user data against the prefetch and show UX for person-format modifications
                    if (this.show_person_format_modifications_ux_elements) {
                        await create_person_format_modifications_ux_elements(
                            response_data,
                            original_user_data,
                            form_container,
                            this.data_hub
                        );
                    }

                    // Make an event with the action body
                    const new_action_event_message_detail_data = {
                        action: data_hub_action_object_to_send_to_api,
                        response: response_data,
                        data_hub_instance: this.data_hub,
                        target: this.data_hub?.target || document
                    };

                    window.dispatchEvent(new CustomEvent('data-hub-new-action-event', {
                        detail: new_action_event_message_detail_data,
                        bubbles: true,
                        composed: true
                    }));

                    // Canonical end-to-end correlation id from the user_data POST. The server
                    // always exposes it in returns.event_uuid (even when a form's custom `returns`
                    // template omits {{event_uuid}}), so forwarding it to /api/post/set_jwt_cookie
                    // below makes that follow-up cookie-set log row correlate to THIS submit in
                    // the data_hub BigQuery action table. Don't mint a client-side fallback —
                    // a random id would log an orphan row that correlates to nothing.
                    const submit_event_uuid = response_data?.returns?.event_uuid || null;

                    // Call data-hub-new-user-data event. Tagged source:'form_submit' so the
                    // SAVE-direction cache-sync listener skips it — submit persists the
                    // post-merge superset explicitly below (this event fires with the raw,
                    // narrower server response, before the wide merge).
                    const new_user_data_event_detail_data = {
                        user_data: response_data?.returns?.user_data || {},
                        jwt_cookie: response_data?.returns?.jwt_cookie || null,
                        event_uuid: submit_event_uuid,
                        prefill_input_selector: this.data_hub?.prefill_input_selector || null,
                        data_hub_instance: this.data_hub,
                        target: this.data_hub?.target || document,
                        source: 'form_submit'
                    };

                    document.dispatchEvent(new CustomEvent('data-hub-new-user-data', {
                        detail: new_user_data_event_detail_data,
                        bubbles: true,
                        composed: true
                    }));

                    // Update data_hub.jwt_cookie (persisted via the cache sync below).
                    if (response_data?.returns?.jwt_cookie) {
                        this.data_hub.jwt_cookie = response_data.returns.jwt_cookie;
                    }

                    // CCPA: server says this person is forgotten (erased/suppressed) →
                    // quick-exit analytics + set the persistent org-scoped DNT flag.
                    // Rare on submit responses (an explicit form submit auto-unforgets
                    // server-side), but any response carrying the marker must gate.
                    apply_server_forgotten_state(this.data_hub, response_data?.returns?.user_data);

                    // Merge the new user_data from the API.
                    // Use merge_user_data_browser for every layer — lodash.merge corrupts complexKeys arrays
                    // (emails[], uuids[], etc.) by index-merging instead of deduping by uniqueProps.
                    if (response_data?.returns?.user_data) {
                        let prior_user_data_for_post_merge = await merge_user_data_browser(
                            {},
                            window.data_hub_user_data || {}
                        );
                        prior_user_data_for_post_merge = await merge_user_data_browser(
                            prior_user_data_for_post_merge,
                            this.data_hub.user_data || {}
                        );
                        prior_user_data_for_post_merge = await merge_user_data_browser(
                            prior_user_data_for_post_merge,
                            data_hub_action_object_to_send_to_api.user_data || {}
                        );
                        this.data_hub.user_data = await merge_user_data_browser(
                            prior_user_data_for_post_merge,
                            response_data.returns.user_data
                        );
                        window.data_hub_user_data = this.data_hub.user_data;
                        re_prefill_all_forms(this.data_hub.user_data, this.prefill_input_selector);
                    }

                    // The one authoritative write for this submit. Routes through the
                    // single-flight cache writer: persists the fullest user_data we now have
                    // (response brought back uuids[], emails[] verification, phone_numbers[]
                    // type/verified flags, etc. not on the instance pre-POST) to the parent LS,
                    // mirrors it to the action-origin LS, and — because jwt_cookie + event_uuid
                    // are present — drives the set_jwt_cookie POST inside the iframe. The
                    // data-hub-new-user-data event above is source:'form_submit' so the save
                    // listener won't double-write the narrower raw response.
                    if (response_data?.returns?.jwt_cookie || response_data?.returns?.user_data) {
                        enqueue_user_data_cache_sync(this.data_hub, {
                            user_data: this.data_hub.user_data || {},
                            jwt_cookie: response_data?.returns?.jwt_cookie || null,
                            event_uuid: submit_event_uuid,
                            source: 'form_submit',
                        });
                    }
                }
            } catch (error) {
                submission_had_error = true;
                const is_timeout = error?.name === 'TimeoutError';
                const internal_message = is_timeout
                    ? `Submission timed out after ${error.elapsed_ms}ms (limit ${error.timeout}ms) on POST ${error.resource}. Server may still complete; watch for a late_response log.`
                    : `Submission error: ${error?.message || error}`;
                const external_message = is_timeout
                    ? "We're still processing your submission — please give it a moment, or try again."
                    : 'Please try again later';
                submission_error_result = return_error_clear_form_loading_state(
                    form_container,
                    internal_message,
                    external_message,
                    this.data_hub
                );
            } finally {
                remove_form_input_loading_state(form_container);
                remove_form_loading_overlay(form_container, this.loading_overlay_selector, this.data_hub);
                remove_submit_button_loading_state(form_container, this.submit_button_selector);
                if (!submission_had_error) {
                    hide_on_submit(this.hide_on_submit_selector, form_container);
                    if (!already_called_return_function) {
                        try {
                            return this.return_function(form_container, response_data, this.data_hub);
                        } catch (error) {
                            // Submission already succeeded server-side; consumer's post-submit hook
                            // failed. Surface to error_function but don't mark the submission errored.
                            handle_consumer_hook_throw(error, form_container, this, 'return_function (post-submit)');
                        }
                    }
                }
            }

            if (submission_had_error) {
                return submission_error_result;
            }

        } catch (error) {
            return return_error_clear_form_loading_state(
                form_container,
                'Error submitting form data: ' + (error.message || error),
                'Please try again later',
                this.data_hub
            );
        }
    }
}
